import React from 'react'

export interface SimpleIconProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  /** The slug of the icon (required) */
  slug: string
  /** Icon color - can be hex, CSS keyword, or any valid color format */
  color?: string
  /** Color for dark mode - used with prefers-color-scheme */
  darkModeColor?: string
  /** Icon height in pixels */
  height?: number | string
  /** Icon width in pixels */
  width?: number | string
  /** Enable auto-sized viewbox for consistent sizing */
  autoViewbox?: boolean
  /** Custom alt text for accessibility */
  alt?: string
}

export const SimpleIcon: React.FC<SimpleIconProps> = ({
  slug,
  color,
  darkModeColor,
  height = 32,
  width = 32,
  autoViewbox = false,
  alt,
  className,
  style,
  ...props
}) => {
  const constructUrl = (): string => {
    if (!slug) return ''

    let url = `https://cdn.simpleicons.org/${encodeURIComponent(slug)}`

    // Handle color parameters
    if (color && darkModeColor) {
      url += `/${encodeURIComponent(color)}/${encodeURIComponent(darkModeColor)}`
    } else if (color) {
      url += `/${encodeURIComponent(color)}`
    } else if (darkModeColor) {
      url += `/_/${encodeURIComponent(darkModeColor)}`
    }

    // Add query parameters
    const params = new URLSearchParams()
    if (autoViewbox) {
      params.append('viewbox', 'auto')
    }

    if (params.toString()) {
      url += `?${params.toString()}`
    }

    return url
  }

  if (!slug) {
    console.warn('SimpleIcon: slug prop is required')
    return null
  }

  return (
    <img
      src={constructUrl()}
      alt={alt || `${slug} icon`}
      height={height}
      width={width}
      className={className}
      style={style}
      loading="lazy"
      {...props}
    />
  )
}

export default SimpleIcon
